import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { useTheme, GTFonts, type Theme } from '../../theme';
import {
  fs,
  formatWithCommas,
  iconSize,
  s,
  v,
  formatMoney,
} from '../../utils/libs';
import type { PaymentMethodBreakdown } from '../../redux/backoffice/dashboardSlice';

type Props = {
  data: PaymentMethodBreakdown | null;
  currencyText: string;
  onViewUnpaidOrders: () => void;
};

/**
 * Outstanding balance owed by customers, with a way to act on it.
 *
 * Deliberately carries no period label: the endpoint returns this as a running
 * balance across every unpaid and partly paid order, not a figure scoped to the
 * dashboard's selected range.
 */
const StillOwedCard: React.FC<Props> = ({
  data,
  currencyText,
  onViewUnpaidOrders,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const amountDue = Number(data?.outstanding?.amount_due || 0);
  const orders = Number(data?.outstanding?.orders || 0);

  if (amountDue <= 0 && orders <= 0) return null;

  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <MaterialCommunityIcons
          name="clock-outline"
          size={fs(15)}
          color={theme.colors.warningDark}
        />
        <Text style={styles.title}>Still Owed To You</Text>
      </View>

      <Text style={styles.amount} numberOfLines={1}>
        {currencyText} {formatMoney(amountDue)}
      </Text>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`View ${formatWithCommas(orders, 0)} unpaid ${
          orders === 1 ? 'order' : 'orders'
        }`}
        onPress={onViewUnpaidOrders}
        style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}>
        <Text style={styles.ctaLabel}>
          View {formatWithCommas(orders, 0)} unpaid{' '}
          {orders === 1 ? 'order' : 'orders'}
        </Text>
        <MaterialCommunityIcons
          name="chevron-right"
          size={iconSize(20)}
          color={theme.colors.textInverse}
        />
      </Pressable>
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      marginTop: v(5),
      marginHorizontal: s(10),
      paddingHorizontal: s(16),
      paddingVertical: s(14),
      ...theme.shadows.sm,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(6),
    },
    title: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    amount: {
      color: theme.colors.warningDarker,
      fontFamily: GTFonts.bold,
      fontSize: fs(26),
      lineHeight: fs(32),
      marginTop: v(6),
    },
    cta: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      columnGap: s(4),
      backgroundColor: theme.colors.successEmerald,
      borderRadius: theme.borderRadius.md,
      height: v(44),
      marginTop: v(12),
    },
    ctaPressed: {
      opacity: 0.85,
    },
    ctaLabel: {
      color: theme.colors.textInverse,
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
  });

export default StillOwedCard;
